import React, {useState} from 'react';
import {
    AppBar,
    Avatar,
    Box,
    Chip,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Toolbar,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    AccountCircle as AccountCircleIcon,
    ChevronLeft as ChevronLeftIcon,
    Dashboard as DashboardIcon,
    Engineering as EngineeringIcon,
    Logout as LogoutIcon,
    Menu as MenuIcon,
    Person as PersonIcon,
    Schedule as ScheduleIcon,
    Settings as SettingsIcon,
    VideoLibrary as VideoLibraryIcon,
} from '@mui/icons-material';
import {useLocation, useNavigate} from 'react-router-dom';
import {routeConfig} from '@/routes/config';
import {useAuth} from '@/hooks/useAuth';
import {authService} from '@/services/authService';
import {ToastContainer} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {useEnsureStorageSelection} from '@/hooks/useEnsureStorageSelection';

const drawerWidth = 240;

interface LayoutProps {
  children: React.ReactNode;
}

const iconMap: Record<string, React.ReactElement> = {
  Dashboard: <DashboardIcon />,
  Engineering: <EngineeringIcon />,
  AccountCircle: <AccountCircleIcon />,
  Schedule: <ScheduleIcon />,
  VideoLibrary: <VideoLibraryIcon />,
  Settings: <SettingsIcon />,
};

export const Layout: React.FC<LayoutProps> = ({ children }) => {
    // Ensure a storage provider is selected globally (default or first active)
    useEnsureStorageSelection();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
    const {user} = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(true);
    const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setDesktopOpen(!desktopOpen);
    }
  };

    const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setUserMenuAnchor(event.currentTarget);
    };

    const handleUserMenuClose = () => {
        setUserMenuAnchor(null);
    };

    const handleLogout = async () => {
        handleUserMenuClose();
        await authService.logout();
    };

    const handleProfile = () => {
        handleUserMenuClose();
        navigate('/settings/profile');
    };

    const getRoleBadgeColor = (role: string | undefined) => {
        switch (role) {
            case 'admin':
                return 'error';
            case 'operator':
                return 'warning';
            case 'viewer':
                return 'info';
            default:
                return 'default';
        }
    };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const isActiveRoute = (path: string) => {
    if (path === '/' && location.pathname === '/') {
      return true;
    }
    if (path !== '/' && location.pathname.startsWith(path)) {
      return true;
    }
    return false;
  };

  const drawer = (
    <Box>
      <Toolbar sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: [1] }}>
        <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, ml: 1 }}>
          InstaPublisher
        </Typography>
        {!isMobile && (
          <IconButton onClick={handleDrawerToggle}>
            <ChevronLeftIcon />
          </IconButton>
        )}
      </Toolbar>
      <Divider />
      <List>
        {routeConfig.map((route) => (
          <ListItem key={route.path} disablePadding>
            <ListItemButton
              selected={isActiveRoute(route.path)}
              onClick={() => handleNavigation(route.path)}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: theme.palette.action.selected,
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  color: isActiveRoute(route.path) ? theme.palette.primary.main : theme.palette.text.secondary,
                }}
              >
                {iconMap[route.icon]}
              </ListItemIcon>
              <ListItemText
                primary={route.name}
                primaryTypographyProps={{
                  fontWeight: isActiveRoute(route.path) ? 600 : 400,
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: desktopOpen ? `calc(100% - ${drawerWidth}px)` : '100%' },
          ml: { md: desktopOpen ? `${drawerWidth}px` : 0 },
          transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar>
          <IconButton color="inherit" aria-label="open drawer" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2 }}>
            <MenuIcon />
          </IconButton>
            <Typography variant="h6" noWrap component="div" sx={{flexGrow: 1}}>
            {routeConfig.find((r) => isActiveRoute(r.path))?.name || 'InstaPublisher'}
          </Typography>

            {/* User info and menu */}
            {user && (
                <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                    <Chip
                        label={user.role}
                        size="small"
                        color={getRoleBadgeColor(user.role) as 'default' | 'error' | 'warning' | 'info'}
                        sx={{textTransform: 'uppercase', fontWeight: 'bold'}}
                    />
                    <Typography variant="body2" sx={{display: {xs: 'none', sm: 'block'}}}>
                        {user.full_name || user.username}
                    </Typography>
                    <IconButton
                        onClick={handleUserMenuOpen}
                        size="small"
                        sx={{ml: 1}}
                        aria-controls={userMenuAnchor ? 'user-menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={userMenuAnchor ? 'true' : undefined}
                    >
                        <Avatar sx={{width: 32, height: 32, bgcolor: theme.palette.primary.main}}>
                            {(user.full_name || user.username || 'U').charAt(0).toUpperCase()}
                        </Avatar>
                    </IconButton>
                    <Menu
                        id="user-menu"
                        anchorEl={userMenuAnchor}
                        open={Boolean(userMenuAnchor)}
                        onClose={handleUserMenuClose}
                        onClick={handleUserMenuClose}
                        transformOrigin={{horizontal: 'right', vertical: 'top'}}
                        anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}
                    >
                        <MenuItem disabled>
                            <Typography variant="caption" color="text.secondary">
                                {user.email}
                            </Typography>
                        </MenuItem>
                        <Divider/>
                        <MenuItem onClick={handleProfile}>
                            <ListItemIcon>
                                <PersonIcon fontSize="small"/>
                            </ListItemIcon>
                            Profile Settings
                        </MenuItem>
                        <MenuItem onClick={handleLogout}>
                            <ListItemIcon>
                                <LogoutIcon fontSize="small"/>
                            </ListItemIcon>
                            Logout
                        </MenuItem>
                    </Menu>
                </Box>
            )}
        </Toolbar>
      </AppBar>
      <Box component="nav" sx={{ width: { md: desktopOpen ? drawerWidth : 0 }, flexShrink: { md: 0 } }}>
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        {/* Desktop drawer */}
        <Drawer
          variant="persistent"
          open={desktopOpen}
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: '100%',
        }}
      >
        <Toolbar />
        {children}
      </Box>
        {/* Global toast container available across all pages */}
        <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
            limit={5}
        />
    </Box>
  );
};
